import {
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Stack,
  Alert,
  TextField,
  Button,
  useMediaQuery,
  Divider,
} from '@mui/material';
import { z } from 'zod';
import React from 'react';
import { toast } from 'sonner';
import { captureException } from '@sentry/react';
import { httpsCallable } from '@firebase/functions';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import type { IntakeStatusBaseSteps } from '@shared/patient-intake-status';

import { functions } from '../../../../../../../firebase-config.js';
import { usePatientIntakeStatusContext } from '../../../../hooks';
import { MHRInformationSection } from './MHRInformationSection.tsx';
import { MHRInstructionsSection } from './MHRInstructionsSection.tsx';

const updatePatientMHRAccessIntakeStatus = httpsCallable<{ patientId: string; accessCode: string }>(
  functions,
  'updatePatientMHRAccessIntakeStatus_v2',
);

const enableMHRAccessSchema = z.object({
  accessCode: z.string().min(4, { message: 'Access code must be 4-8 characters long' }).max(8, {
    message: 'Access code must be 4-8 characters long',
  }),
});

export type EnableMHRAccessFormData = z.infer<typeof enableMHRAccessSchema>;

type EnableMHRAccessModalProps = {
  step: IntakeStatusBaseSteps['MHR_ACCESS'];
  isOpen: boolean;
  onClose: () => void;
};

const EnableMHRAccessPatientModal: React.FC<EnableMHRAccessModalProps> = ({ isOpen, onClose }) => {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const { patientId } = usePatientIntakeStatusContext();

  const methods = useForm<EnableMHRAccessFormData>({
    resolver: zodResolver(enableMHRAccessSchema),
    defaultValues: {
      accessCode: '',
    },
  });

  const {
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = methods;

  const onSubmit = async (data: EnableMHRAccessFormData) => {
    try {
      await updatePatientMHRAccessIntakeStatus({ patientId, accessCode: data.accessCode });

      onClose();
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error(error);
      }

      captureException(error, {
        tags: { issueIn: 'EnableMHRAccessModal.onSubmit' },
        extra: { patientId, data },
      });

      toast.error('An error occurred', {
        description:
          'Failed to enable MHR access. Please try again later. If the problem persists, please contact support.',
      });
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle>My Health Record - Granting Access</DialogTitle>

      <DialogContent>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack gap={2}>
              <MHRInformationSection />

              <MHRInstructionsSection />

              <Divider />
              <Stack gap={2} mt={1}>
                <Stack gap={1}>
                  <Typography variant="caption">
                    Enter the access code you used in the instructions above. If you generated a code, it will
                    automatically be filled in for you. Please ensure you enter the correct code if you created one
                    yourself.
                  </Typography>
                  <Controller
                    control={methods.control}
                    name="accessCode"
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Access Code"
                        disabled={isSubmitting}
                        helperText={errors.accessCode?.message}
                        error={!!errors.accessCode}
                        placeholder="Enter the access code"
                      />
                    )}
                  />
                </Stack>

                <Button type="submit" variant="contained" loading={isSubmitting} disabled={!isValid}>
                  Share MHR Access Code
                </Button>
              </Stack>
            </Stack>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};

const EnableMHRAccessHealthcareProviderModal: React.FC<EnableMHRAccessModalProps> = ({ isOpen, onClose }) => (
  <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Typography variant="h5">My Health Record Access - Granting Access</Typography>
    </DialogTitle>

    <DialogContent sx={{ pb: 3 }}>
      <Alert severity="info">
        The patient has been provided instructions on how to enable MHR access. Once they have completed the steps, you
        will be able to view their My Health Record, either directly, or with the provided access code.
      </Alert>
    </DialogContent>
  </Dialog>
);

export const EnableMHRAccessModal: React.FC<EnableMHRAccessModalProps> = ({ step, isOpen, onClose }) => {
  const { isHealthcareProvider } = usePatientIntakeStatusContext();

  if (isHealthcareProvider) {
    return <EnableMHRAccessHealthcareProviderModal step={step} isOpen={isOpen} onClose={onClose} />;
  }

  return <EnableMHRAccessPatientModal step={step} isOpen={isOpen} onClose={onClose} />;
};
